/**
 * @fileoverview PropertyCard component with improved structure and readability
 * @author Housefolios
 * @modified David Helmick
 * @modified 2025-09-03 13:35:28
 * @description Refactored PropertyCard component with improved structure and readability
 */

import React from 'react'
import {
  ContextMenu,
  MenuItem,
  ContextMenuTrigger,
} from 'react-contextmenu'

import DeleteProperty from '../DeleteProperty'
import DuplicateProperty from '../DuplicateProperty'
import Analyze from '@/housefolios-components/Marketplace/Analyze'

// Centralized lodash imports - much cleaner!
import {
  sum,
  find,
  merge,
  cloneDeep,
  get,
  includes,
  isEmpty,
} from '@/utils/lodash'

import {
  Grid,
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  ListItem,
  Tooltip,
  Typography,
  Box,
} from '@mui/material'

import ContactAgent from '@/housefolios-components/Marketplace/ContactAgent'
import withSession from '@/housefolios-components/Session/withSession'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import TrashProperty from '../TrashProperty'
import RestoreProperty from '../RestoreProperty'
import ShareProperty from '../ShareProperty'
import { DEFAULT_PREFERENCES } from '@/housefolios-components/OrganizationSettings/OrganizationPreferences'
import CriticalFields from '@/housefolios-components/Dialogs/CriticalFields'
import SubmissionApproval from '@/housefolios-components/Marketplace/SubmissionApproval'

import {
  currencyFormat,
  numberFormat,
  percentageFormat,
} from '@/utils/number'
import SubtoPITICash from '@/housefolios-components/Dialogs/SubtoPITI/Cash/'
import SubtoPITICreative from '@/housefolios-components/Dialogs/SubtoPITI/Creative/'
import PropertyInformationDialogs from '@/housefolios-components/Dialogs/PropertyInformationDialogs'

// Import custom hooks and components
import { usePropertyCard } from './hooks/usePropertyCard'
import PropertyCardImages from './components/PropertyCardImages'
import PropertyCardAnalytics from './components/PropertyCardAnalytics'
import { CARD_DIMENSIONS, getPropertyCardLayoutSx } from './constants'
import { getProformaLink } from '@/utils/proforma'
import { propertyCardRibbonSx } from '@/theme/skinSx'

function PropertyCardComponent(props) {
  const { property, propertySearch, public: isPublic = false } = props

  // Use custom hook for business logic
  const {
    heart,
    openCriticalFields,
    redirectToEdit,
    openPITICashData,
    openPITICreativeData,
    marketplaceOwned,
    owned,
    pricePerSqft,
    createdAt,
    daysUntilDeleted,
    listDate,
    realeflowSoldDate,
    analytics,
    handleOpenCriticalFields,
    handleCloseCriticalFields,
    toggleHeart,
    setOpenPITICashData,
    setOpenPITICreativeData,
    updateProperty,
  } = usePropertyCard(property, props)

  // Get plan images
  const plan =
    find(props.session?.me?.activeOrg?.plans || [], {
      value: property.plan,
    }) || {}
  const planImages = plan.images || []

  // Get preferences
  let preferences = merge(
    cloneDeep(DEFAULT_PREFERENCES),
    props?.session?.me?.activeOrg?.memberPreferences,
    props?.session?.me?.activeOrg?.preferences,
  )

  const getPropertyUrl = () => {
    if (props.trash) return '/trash'
    if (isPublic) {
      const subdomainParam = props.subdomainParam || ''
      return `/public-property-details/${property._id}/Summary${subdomainParam}`
    }
    if (props.marketplace && !marketplaceOwned) return '/marketplace'

    return `/property-details/${property._id}/Summary`
  }

  const handleCardClick = (event) => {
    if (props.jv && property.jvStatus === 'New') {
      updateProperty({
        variables: {
          id: property._id,
          propertyInput: {
            jvStatus: 'In Process',
            jvTimeline: {
              ...property.jvTimeline,
              viewed: new Date(),
            },
          },
        },
      })
    } else if (props.lender && property.lenderStatus === 'New') {
      updateProperty({
        variables: {
          id: property._id,
          propertyInput: {
            lenderStatus: 'In Process',
            lenderTimeline: {
              ...property.lenderTimeline,
              viewed: new Date(),
            },
          },
        },
      })
    } else if (
      (!props.shared || props.sharedProperties) &&
      ((props.marketplace && !marketplaceOwned) || props.trash)
    ) {
      event.preventDefault()
      if (
        (props.propertySearch &&
          property.marketplaces &&
          !isEmpty(property.marketplaces.approvedMarketplace)) ||
        (!marketplaceOwned &&
          !props.propertySearch &&
          !props.sharedProperties &&
          !props.trash)
      ) {
        props.selectMarketplaceProperty(property._id)(event)
      } else {
        props.selectProperty(property._id)(event)
      }
    } else {
      event.preventDefault()
      handleOpenCriticalFields()
    }
  }

  const renderRibbons = () => (
    <>
      {owned && (
        <Box component="span" sx={{
          position: "absolute",
          zIndex: 8,
          width: 160,
          height: 160,
          overflow: "hidden",
          "& > small": {
            position: "absolute",
            display: "block",
            width: "100%",
            padding: "0.25rem",
            textAlign: "center",
            textTransform: "uppercase",
            fontWeight: "bold",
            fontSize: "0.769rem",
            transform: "rotate(45deg)",
            boxShadow: "0 3px 6px -3px rgba(7, 9, 25, 0.5)",
            top: 27,
            left: 37,
            color: "#fff",
            backgroundColor: "#368cff",
          },
          top: 0,
          right: 0,
        }}>
          <small>
            <Typography style={{ fontSize: 14, fontWeight: 600 }}>
              Owned
            </Typography>
          </small>
        </Box>
      )}
      {props.propertySearch &&
        !marketplaceOwned &&
        !props.shared &&
        property.marketplaces &&
        !isEmpty(property.marketplaces.approvedMarketplace) && (
          <Box component="span" sx={{
            position: "absolute",
            zIndex: 8,
            width: 160,
            height: 160,
            overflow: "hidden",
            "& > small": {
              position: "absolute",
              display: "block",
              width: "100%",
              padding: "0.25rem",
              textAlign: "center",
              textTransform: "uppercase",
              fontWeight: "bold",
              fontSize: "0.769rem",
              transform: "rotate(45deg)",
              boxShadow: "0 3px 6px -3px rgba(7, 9, 25, 0.5)",
              top: 27,
              left: 37,
              color: "#fff",
              backgroundColor: "#1bc943",
            },
            top: 0,
            right: 0,
          }}>
            <small style={{ textAlign: 'center' }}>
              <Typography style={{ fontSize: 14, fontWeight: 600 }}>
                Marketplace
              </Typography>
            </small>
          </Box>
        )}
      {props.marketplace &&
        !props.propertySearch &&
        !props.trash &&
        property?.acquisition &&
        property?.marketplaces &&
        isPublic &&
        !isEmpty(property?.marketplaces.extraTags) && (
          <Box component="span" sx={{
            position: "absolute",
            zIndex: 8,
            width: 160,
            height: 160,
            overflow: "hidden",
            "& > small": {
              position: "absolute",
              display: "block",
              width: "100%",
              padding: "0.25rem",
              textAlign: "center",
              textTransform: "uppercase",
              fontWeight: "bold",
              fontSize: "0.769rem",
              transform: "rotate(-45deg)",
              boxShadow: "0 3px 6px -3px rgba(7, 9, 25, 0.5)",
              top: 27,
              left: -37,
              color: "#fff",
              backgroundColor: "#368cff",
            },
            top: 0,
            left: 0,
          }}>
            <small style={{ textAlign: 'center' }}>
              <Typography
                style={{
                  fontSize:
                    property?.marketplaces.extraTags[0].length > 13
                      ? 10
                      : 14,
                  fontWeight: 600,
                }}
              >
                {property?.marketplaces.extraTags[0]}
              </Typography>
            </small>
          </Box>
        )}
      {props.marketplace &&
        !props.propertySearch &&
        !props.trash &&
        property.acquisition && (
          <Box
            component="span"
            sx={propertyCardRibbonSx(property?.status)}
          >
            <small style={{ textAlign: 'center' }}>
              <Typography
                style={{
                  fontSize:
                    property?.status && property?.status.length > 13
                      ? 10
                      : 14,
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: 'transparent',
                }}
              >
                {property?.status}
              </Typography>
            </small>
          </Box>
        )}
    </>
  )

  const renderStatusChips = () => (
    <div style={{position:"absolute",right:"0.75rem",top:"0.75rem",zIndex:2}}>
      {props.jv && (
        <Chip
          label={property.jvStatus}
          style={{
            maxWidth: 'none',
            position: 'absolute',
            right: 20,
            top: 20,
            backgroundColor:
              property.jvStatus === 'Rejected'
                ? 'red'
                : property.jvStatus === 'Accepted'
                  ? '#00bf00'
                  : 'orange',
            color: 'white',
            fontWeight: 600,
          }}
        />
      )}
      {props.lender && (
        <Chip
          label={property.lenderStatus}
          style={{
            maxWidth: 'none',
            position: 'absolute',
            right: 20,
            top: 20,
            backgroundColor:
              property.lenderStatus === 'Rejected'
                ? 'red'
                : property.lenderStatus === 'Accepted'
                  ? '#00bf00'
                  : 'orange',
            color: 'white',
            fontWeight: 600,
          }}
        />
      )}
    </div>
  )

  const getMarketplacePrice = () => {
    let resellPrice = ''
    switch (property.marketplaceStrategy) {
      case 'Buy & Hold':
        resellPrice = get(
          property,
          'acquisition.analysis.resellPrice',
        )
        break
      case 'Fix & Flip':
        resellPrice = get(
          property,
          'acquisition.analysis.resellPriceFixFlip',
        )
        break
      case 'Turnkey End Buyer':
        resellPrice = get(
          property,
          'acquisition.analysis.resellPriceTurnkey',
        )
        break
      case 'BRRRR':
        resellPrice = get(
          property,
          'acquisition.analysis.resellPriceBRRRR',
        )
        break
      case 'Short Term Rental':
        resellPrice = get(
          property,
          'acquisition.analysis.resellPriceSTR',
        )
        break
      case 'Wholesale':
        resellPrice = get(
          property,
          'acquisition.analysis.resellPriceWholesale',
        )
        break
      case 'Build to Rent':
        resellPrice = get(
          property,
          'acquisition.analysis.resellPriceB2R',
        )
        break
      case 'BOR':
        resellPrice = get(
          property,
          'acquisition.analytics.BOR.arvTotalBOR',
        )
        break
    }
    return resellPrice
  }

  const renderPropertyContent = () => (
    <CardContent
      sx={[
        {
          position: "relative",
          "& > .avatar-icon-wrapper": {
            margin: 0,
            position: "absolute",
            height: 120,
            width: 120,
            top: -60,
            zIndex: 3,
            "& .avatar-icon": { height: "100%", width: "100%", boxShadow: "0 0.46875rem 2.1875rem rgba(59, 62, 102, 0.03), 0 0.9375rem 1.40625rem rgba(59, 62, 102, 0.03), 0 0.25rem 0.53125rem rgba(59, 62, 102, 0.05), 0 0.125rem 0.1875rem rgba(59, 62, 102, 0.03)" },
            "& img": { maxWidth: "100%", height: "auto", width: "100%" },
            "&.avatar-icon-wrapper--sm, &--sm": { height: 80, width: 80, top: -40 },
          },
          "&.text-center > .avatar-icon-wrapper": {
            left: "50%",
            marginLeft: -60,
            "& .avatar-icon": { margin: 0 },
            "&.avatar-icon-wrapper--sm, &--sm": { marginLeft: -40 },
          },
        },
        {
          display: 'flex',
          flexDirection: 'column',
          pt: 1,
          px: 2,
          pb: 1,
          '&:last-child': { pb: 1 },
        },
      ]}
      style={{
        ...(props.marketplace
          ? { flex: '1 1 auto', minHeight: 0 }
          : { height: CARD_DIMENSIONS.CARD_HEIGHT_DEFAULT  }),
      }}
    >
      <Grid
        container
        direction="column"
        sx={{
          justifyContent: 'flex-start',
          gap: 0.5,
          width: '100%',
        }}
      >
        {/* Price and Property Details */}
        <Grid container style={{ width: '100%' }}>
          <Grid
            size={12}
            container
            spacing={1}
            wrap="wrap"
            sx={{
              justifyContent: 'space-between',
              alignItems: 'baseline',
              width: '100%',
              columnGap: 1,
            }}
          >
            <Grid size="auto" sx={{ minWidth: 0 }}>
              <Box
                component="h5"
                style={{ fontSize: 26, margin: 0 }}
                sx={{ fontWeight: 700 }}
                >
                {currencyFormat.format(
                  propertySearch && !property.marketplaces
                    ? property.price
                    : props.marketplace && !props.trash
                      ? getMarketplacePrice() || 0
                      : get(
                          property,
                          'acquisition.analysis.listPrice',
                        ) || 0,
                )}
              </Box>
            </Grid>
            <Grid size="auto" sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Typography sx={[{ textAlign: { sm: 'right' } }, { fontSize: "0.875rem !important" }]}>
                {sum(property.beds) || '-'} bd |{' '}
                {sum(property.baths) || '-'} ba |{' '}
                {sum(property.sqft)
                  ? numberFormat.format(Number(sum(property.sqft)))
                  : '-'}{' '}
                sqft |{' '}
                {propertySearch && property.yearBuilt
                  ? property.yearBuilt
                  : property.year || '-'}{' '}
                yr
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        {/* Address */}
        <Grid style={{ width: '100%' }}>
          <Grid
            container
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Grid>
              <Box component="h5" sx={[{ fontWeight: 700, m: 0 }, { fontSize: "0.875rem !important" }]}>
                {!property.hideAddress || !isPublic
                  ? property.address + ', '
                  : ''}
                {property.city}, {property.state} {property.zip}
              </Box>
            </Grid>
          </Grid>
        </Grid>

        {/* Additional Details */}
        {!props.marketplace && !owned && (
          <Grid
            container
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Grid>
              <Typography sx={{ fontSize: "0.875rem !important" }}>
                Date Added: {createdAt}
              </Typography>
            </Grid>
            {props.submitted && (
              <Grid>
                <Typography sx={{ fontSize: "0.875rem !important" }}>
                  Submitted By:{' '}
                  {property.marketplaceSubmittedBy &&
                  property.marketplaceSubmittedBy.profile
                    ? `${property.marketplaceSubmittedBy.profile.firstname} ${property.marketplaceSubmittedBy.profile.lastname} `
                    : ''}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}

        {props.marketplace &&
          !props.propertySearch &&
          !props.trash &&
          !owned && (
            <Grid
              container
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Grid>
                <Typography sx={{ fontSize: "0.875rem !important" }}>
                  List Date: {String(listDate)}
                </Typography>
              </Grid>
            </Grid>
          )}

        {realeflowSoldDate && (
          <Grid
            container
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Grid>
              <Typography sx={{ fontSize: "0.875rem !important" }}>
                Last Sold Date: {realeflowSoldDate}
              </Typography>
            </Grid>
          </Grid>
        )}

        {props.trash && (
          <Grid>
            <Typography color="error" sx={{ fontSize: "0.875rem !important" }}>
              Deletes in: {daysUntilDeleted} days
            </Typography>
          </Grid>
        )}

        {/* Analytics */}
        <Grid style={{ width: '100%' }}>
          <PropertyCardAnalytics
            analytics={analytics}
            propertySearch={propertySearch}
            pricePerSqft={pricePerSqft}
            averagePerSqft={props.averagePerSqft}
            currencyFormat={currencyFormat}
            percentageFormat={percentageFormat}
          />
        </Grid>

        {/* Duplicate Warning */}
        {property.duplicate && (
          <Grid style={{ width: '100%', marginBottom: '8px' }}>
            <div
              style={{
                backgroundColor: '#ff6666',
                color: 'white',
                padding: '4px 8px',
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '12px',
                borderRadius: '2px',
              }}
            >
              DUPLICATE
            </div>
          </Grid>
        )}
      </Grid>
    </CardContent>
  )

  const renderMarketplaceActions = () => {
    if (!props.marketplace) return null

    const proformaType = get(
      property,
      'marketplaces.proformaType',
      get(property, 'marketplaceStrategy', 'marketing'),
    )
    // Build proforma link using shared helper to ensure correct view (e.g., marketing for Turnkey)
    const proformaLink = getProformaLink(
      property._id,
      [proformaType],
      false,
    )

    return (
      <>
        <Divider />
        <CardActions
          sx={{
            display: 'block',
            width: '100%',
            flex: '0 0 auto',
            px: 1.5,
            pt: 1,
            pb: 1.5,
            boxSizing: 'border-box',
          }}
        >
          <Grid
            container
            direction="row"
            spacing={1}
            sx={{
              width: '100%',
              alignItems: 'stretch',
              '--Grid-parent-columns': '12',
            }}
          >
            {isPublic ||
            (props.marketplace &&
              !marketplaceOwned &&
              !props.trash &&
              props?.session?.me.activeOrg.parentCorp &&
              !props.propertySearch) ? (
              <>
                <Grid size={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    component="a"
                    href={proformaLink}
                    target="_blank"
                  >
                    <FontAwesomeIcon icon={['far', 'file-lines']} style={{ mr: '0.5rem' }} sx={{ fontSize: "1rem !important" }} />
                    <Typography>Proforma</Typography>
                  </Button>
                </Grid>
              </>
            ) : props.submitted ? (
              <SubmissionApproval
                propertyIds={[property._id]}
                marketplace={props.marketplace}
                removeProperty={
                  includes(props.selected, property._id)
                    ? props.selectMarketplaceProperty(property._id)
                    : null
                }
              />
            ) : (
              <>
                <Grid size={6}>
                  <ContactAgent
                    property={property}
                    marketplace={props.marketplace}
                  />
                </Grid>
                <Grid size={6}>
                  <Analyze
                    selected={
                      property.marketplaces &&
                      !isEmpty(
                        property.marketplaces.approvedMarketplace,
                      )
                        ? []
                        : [property._id]
                    }
                    marketplaceSelected={
                      property.marketplaces &&
                      !isEmpty(
                        property.marketplaces.approvedMarketplace,
                      )
                        ? [property._id]
                        : []
                    }
                    properties={props.properties}
                    portfolios={props.portfolios}
                    propertySearch={props.propertySearch}
                  />
                </Grid>
              </>
            )}

            {props.marketplace &&
              marketplaceOwned &&
              !props.trash && (
                <Grid size={12}>
                  <Button
                    fullWidth
                    component="a"
                    href={`/property-details/${property._id}/Summary`}
                    onClick={(event) => {
                      event.preventDefault()
                      handleOpenCriticalFields()
                    }}
                    variant="contained"
                    color="primary"
                  >
                    View
                  </Button>
                </Grid>
              )}

            {props.trash && (
              <>
                <Grid size={6}>
                  <RestoreProperty
                    button
                    portfolios={props.portfolios}
                    propertyId={property._id}
                    portfolioId={
                      property.portfolio ? property.portfolio._id : ''
                    }
                  />
                </Grid>
                <Grid size={6}>
                  <DeleteProperty
                    button
                    portfolios={props.portfolios}
                    propertyId={property._id}
                    portfolioId={props.selectedPortfolio}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </CardActions>
      </>
    )
  }

  const renderContextMenu = () => {
    if (isPublic || props.marketplace || props.shared || props.trash)
      return null

    return (
      <Box sx={{ position: 'relative' }}>
        {/* @ts-ignore */}
        <ContextMenu id={`ContextMenuTrigger_${property._id}`} sx={[{
          width: "100%",
          "& .MuiListItem-root, & .MuiListItemButton-root": { borderRadius: "0.65rem", width: "100%" },
          "& .MuiListItem-root, & .MuiListItemButton-root, .MuiList-root.nav-transparent-alt .MuiListItem-root, .MuiList-root.nav-transparent-alt .MuiListItemButton-root": { width: "100%" },
          "& .MuiListItem-root.MuiListItem-button, & .MuiListItemButton-root": { borderRadius: "0.65rem", width: "100%" },
        }, { backgroundColor: '#fff', borderRadius: '0.29rem', flexDirection: 'column', }, {
          boxShadow: "0 0.18rem 0.3rem rgba(166, 166, 185, 0.5), 0 0.326rem 3rem rgba(79, 80, 98, 0.3) !important",
          "& .MuiListItem-root.MuiListItem-button.Mui-selected, & .MuiListItem-root.MuiListItem-button:hover, & .MuiListItemButton-root.Mui-selected, & .MuiListItemButton-root:hover": {
            color: "#4191ff",
            background: "rgba(65, 145, 255, 0.05)",
            "&::before": { background: "#4191ff" },
          },
        }]}>
          <ListItem
            key="edit"
            component="a"
            href={`/property-details/${property._id}/Edit`}
            onClick={(event) => {
            event.preventDefault()
            handleOpenCriticalFields(true)
            }}
            sx={{ borderRadius: '0.29rem' }}
            >
            <FontAwesomeIcon icon={['fas', 'pen']} style={{ marginRight: 10 }} sx={{ fontSize: "1.15rem !important" }} />
            <span>Edit Property</span>
          </ListItem>
          {/* @ts-ignore */}
          <MenuItem>
            <DuplicateProperty
              rightClickMenu={true}
              portfolios={props.portfolios}
              selectedPortfolio={props.selectedPortfolio}
              propertyId={property._id}
            />
          </MenuItem>
          {/* @ts-ignore */}
          <MenuItem>
            <ShareProperty
              rightClickMenu={true}
              propertyId={property._id}
              portfolios={props.portfolios}
            />
          </MenuItem>
          {/* @ts-ignore */}
          <MenuItem>
            <TrashProperty
              rightClickMenu={true}
              portfolios={props.portfolios}
              propertyId={property._id}
              portfolioId={props.selectedPortfolio}
            />
          </MenuItem>
        </ContextMenu>
      </Box>
    )
  }

  return (
    <Grid size={{ xs: 12, md: props.fullWidth ? 12 : isPublic ? 4 : 6, xl: props.fullWidth ? 12 : 4, }} sx={[getPropertyCardLayoutSx({ fullWidth: props.fullWidth, isPublic,  }), { marginBottom: "24px" }]}>
      <Tooltip
        title={
          (props.marketplace && !marketplaceOwned) || props.trash
            ? ''
            : props.shared || marketplaceOwned
              ? 'Click to see analysis'
              : 'Click to see analysis or right click for more options'
        }
      >
        <Box style={{ backgroundColor: 'white', position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', }} sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          backgroundColor: "#fff",
          borderRadius: "0.65rem",
          boxShadow: "0 0.46875rem 2.1875rem rgba(59, 62, 102, 0.03), 0 0.9375rem 1.40625rem rgba(59, 62, 102, 0.03), 0 0.25rem 0.53125rem rgba(59, 62, 102, 0.05), 0 0.125rem 0.1875rem rgba(59, 62, 102, 0.03)",
          "button&": { outline: "none" },
          wordWrap: "break-word",
          backgroundClip: "border-box",
          border: "0 solid rgba(79, 80, 98, 0.3)",
          "html[data-color-mode=\"dark\"] &": { backgroundColor: "#1e1e1e !important", color: "rgba(255, 255, 255, 0.87)" },
          transition: "all 0.2s ease-in-out !important",
          "&:hover": { boxShadow: "0 0.56875rem 3.3875rem rgba(59, 62, 102, 0.05), 0 0.9975rem 2.40625rem rgba(59, 62, 102, 0.07), 0 0.35rem 1.23125rem rgba(59, 62, 102, 0.05), 0 0.225rem 1.1875rem rgba(59, 62, 102, 0.09)", transform: "translateY(-15px)" },
          "&.card-box-hover:hover": { boxShadow: "0 0.56875rem 3.3875rem rgba(59, 62, 102, 0.05), 0 0.9975rem 2.40625rem rgba(59, 62, 102, 0.07), 0 0.35rem 1.23125rem rgba(59, 62, 102, 0.05), 0 0.225rem 1.1875rem rgba(59, 62, 102, 0.09)", transform: "translateY(0)" },
        }}>
          {renderRibbons()}

          {!isPublic ? (
            /* @ts-ignore */
            <ContextMenuTrigger
              id={`ContextMenuTrigger_${property._id}`}
              attributes={{
                style: {
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '1 1 auto',
                  minHeight: 0,
                },
              }}
            >
              <a
                href={getPropertyUrl()}
                onClick={handleCardClick}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '1 1 auto',
                  minHeight: 0,
                  color: 'inherit',
                  textDecoration: 'none',
                }}
              >
                {renderStatusChips()}

                <PropertyCardImages
                  property={property}
                  planImages={planImages}
                  props={props}
                  marketplaceOwned={marketplaceOwned}
                  owned={owned}
                  handleOpenCriticalFields={handleOpenCriticalFields}
                  toggleHeart={toggleHeart}
                  heart={heart}
                  updateProperty={updateProperty}
                  openPITICashData={openPITICashData}
                  setOpenPITICashData={setOpenPITICashData}
                  openPITICreativeData={openPITICreativeData}
                  setOpenPITICreativeData={setOpenPITICreativeData}
                />

                {/* Checkbox for selection */}
                <div style={{position:"absolute",left:"1rem",top:"0.5rem"}}>
                  {(!props.shared || props.sharedProperties) && (
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation()
                        event.preventDefault()
                        if (
                          (props.propertySearch &&
                            property.marketplaces &&
                            !isEmpty(
                              property.marketplaces
                                .approvedMarketplace,
                            )) ||
                          (!marketplaceOwned &&
                            !props.propertySearch &&
                            !props.sharedProperties &&
                            !props.trash)
                        ) {
                          props.selectMarketplaceProperty(
                            property._id,
                          )(event)
                        } else {
                          props.selectProperty(property._id)(event)
                        }
                      }}
                      size="large"
                    >
                      <Checkbox
                        checked={includes(
                          props.selected,
                          property._id,
                        )}
                        sx={{
                          height: 20,
                          width: 20,
                          backgroundColor: 'white',
                          borderRadius: 0,
                          'html[data-color-mode="dark"] &': {
                            backgroundColor: 'rgba(30, 30, 30, 0.88)',
                            color: 'rgba(255, 255, 255, 0.75)',
                            '&.Mui-checked': { color: '#90caf9' },
                          },
                        }}
                      />
                    </IconButton>
                  )}
                </div>

                {renderPropertyContent()}
              </a>
            </ContextMenuTrigger>
          ) : (
            <a
              href={getPropertyUrl()}
              onClick={handleCardClick}
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 auto',
                minHeight: 0,
                color: 'inherit',
                textDecoration: 'none',
              }}
            >
              {renderStatusChips()}

              <PropertyCardImages
                property={property}
                planImages={planImages}
                props={props}
                marketplaceOwned={marketplaceOwned}
                owned={owned}
                handleOpenCriticalFields={handleOpenCriticalFields}
                toggleHeart={toggleHeart}
                heart={heart}
                updateProperty={updateProperty}
                openPITICashData={openPITICashData}
                setOpenPITICashData={setOpenPITICashData}
                openPITICreativeData={openPITICreativeData}
                setOpenPITICreativeData={setOpenPITICreativeData}
              />

              {/* Checkbox for selection */}
              <div style={{position:"absolute",left:"1rem",top:"0.5rem"}}>
                {(!props.shared || props.sharedProperties) && (
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation()
                      event.preventDefault()
                      if (
                        (props.propertySearch &&
                          property.marketplaces &&
                          !isEmpty(
                            property.marketplaces.approvedMarketplace,
                          )) ||
                        (!marketplaceOwned &&
                          !props.propertySearch &&
                          !props.sharedProperties &&
                          !props.trash)
                      ) {
                        props.selectMarketplaceProperty(property._id)(
                          event,
                        )
                      } else {
                        props.selectProperty(property._id)(event)
                      }
                    }}
                    size="large"
                  >
                    <Checkbox
                      checked={includes(props.selected, property._id)}
                      sx={{
                        height: 20,
                        width: 20,
                        backgroundColor: 'white',
                        borderRadius: 0,
                        'html[data-color-mode="dark"] &': {
                          backgroundColor: 'rgba(30, 30, 30, 0.88)',
                          color: 'rgba(255, 255, 255, 0.75)',
                          '&.Mui-checked': { color: '#90caf9' },
                        },
                      }}
                    />
                  </IconButton>
                )}
              </div>

              {renderPropertyContent()}
            </a>
          )}
          {renderMarketplaceActions()}
        </Box>
      </Tooltip>

      {renderContextMenu()}

      {/* Dialogs */}
      <PropertyInformationDialogs
        open={openCriticalFields}
        onClose={handleCloseCriticalFields}
        property={property}
        shared={props.shared}
        url={
          redirectToEdit
            ? `/property-details/${property._id}/Edit`
            : getPropertyUrl()
        }
      />

      <SubtoPITICash
        open={openPITICashData}
        onClose={() => setOpenPITICashData(false)}
        propertyId={property?._id}
        address={property?.address}
        city={property.city}
        state={property.state}
        zipcode={property?.zip}
      />

      <SubtoPITICreative
        open={openPITICreativeData}
        onClose={() => setOpenPITICreativeData(false)}
        propertyId={property?._id}
        address={property?.address}
        city={property.city}
        state={property.state}
        zipcode={property?.zip}
      />
    </Grid>
  )
}

// Create a version with session for private use
const PropertyCardWithSession = withSession(PropertyCardComponent)

// Create a version without session for public use
const PropertyCardPublic = (props) => (
  <PropertyCardComponent {...props} public={true} />
)

// Export both versions
export { PropertyCardPublic }
export default PropertyCardWithSession
